'use client'

import type React from 'react'
import { useMemo, useCallback, useState, memo } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'
import ChatBox, { type ChatBoxProps, type FileAttachment as ChatBoxFileAttachment, type ChatAuthor } from '@/components/ui/chat/ChatBox/ChatBox'
import MarkdownProcessor from './MarkdownProcessor'
import type { DisplayMessage, Author, UploadedFileAttachment } from '@/core/models/medconnect-agent.model'
import { useDeferredValueCompat } from '@/hooks/useDeferredValueCompat'

interface AgentMessageDisplayProps {
  message: DisplayMessage
  humanAuthor: Author
  aiAuthor: Author
  isStreaming?: boolean
  /** Compact Cloudflare-style rendering for side panel */
  compact?: boolean
}

/**
 * Strips tool_interaction tags and code blocks for copy.
 */
function cleanForCopy(content: string): string {
  return content
    .replace(/\s*\[tool_interaction\]\([^)]+\)\s*/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Compact message renderer — Cloudflare-style.
 * User: right-aligned dark pill, no label/timestamp.
 * AI: left-aligned, no bubble, just markdown on white. Action icons below.
 */
const CompactMessage: React.FC<{ message: DisplayMessage; isStreaming: boolean }> = ({ message, isStreaming }) => {
  const deferredContent = useDeferredValueCompat(message.content)
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(cleanForCopy(message.content))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }, [message.content])

  if (message.type === 'error') {
    return (
      <Box sx={{ my: 1, px: 1.5, py: 1, bgcolor: 'error.lighter', color: 'error.dark', borderRadius: 1, fontSize: '0.85rem' }}>
        {message.content}
      </Box>
    )
  }
  if (message.type === 'tool') return null

  // ── User message ──
  if (message.type === 'human') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1.5 }}>
        <Box
          sx={{
            maxWidth: 'min(85%, 580px)',
            backgroundColor: '#1a1a1a',
            color: '#fff',
            borderRadius: '18px 18px 4px 18px',
            px: 2,
            py: 1.25,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Box>
      </Box>
    )
  }

  // ── AI message ──
  return (
    <Box sx={{ my: 1 }}>
      {/* Markdown on white — MarkdownProcessor handles all compact styling */}
      <MarkdownProcessor content={deferredContent} compact />

      {/* Action icons — show after streaming completes */}
      {!isStreaming && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          <Tooltip title="Copy">
            <IconButton size="small" onClick={handleCopy} sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
              {copied ? <CheckIcon sx={{ fontSize: 16 }} color="success" /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Good response">
            <IconButton size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
              <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bad response">
            <IconButton size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
              <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  )
}

export const AgentMessageDisplay: React.FC<AgentMessageDisplayProps> = ({ message, humanAuthor, aiAuthor, isStreaming = false, compact = false }) => {
  const deferredContent = useDeferredValueCompat(message.content)

  // ── Compact mode (side panel) ──
  if (compact) {
    return (
      <Box id={`message-${message.id}`} sx={{ px: 1.5 }}>
        <CompactMessage message={message} isStreaming={isStreaming} />
      </Box>
    )
  }

  // ── Full mode (chat page) — unchanged ──
  const chatBoxInputFiles: ChatBoxFileAttachment[] | undefined = (() => {
    if (!message.attachedFiles || message.attachedFiles.length === 0) return undefined
    return message.attachedFiles.map((file) => ({
      filename: file.filename,
      key: file.key,
      name: file.filename,
      type: file.objectType || 'file',
      url: file.url,
    }))
  })()

  if (message.type === 'error') {
    return <Box sx={{ my: 1, p: 1, bgcolor: 'error.lighter', color: 'error.dark', borderRadius: 1 }}>{message.content}</Box>
  }
  if (message.type === 'tool') return null

  const authorDetails = message.type === 'human' ? humanAuthor : aiAuthor
  const isAIMessage = message.type === 'ai'
  const chatBoxProps: ChatBoxProps = {
    isMine: message.type === 'human',
    data: {
      text: message.type === 'human' ? message.content : '',
      attachedFiles: chatBoxInputFiles,
    },
    date: message.createdAt || message.timestamp,
    author: authorDetails as ChatAuthor,
    reads: [],
    showCopyButton: isAIMessage && !isStreaming,
    copyContent: message.content,
  }

  return (
    <Box sx={{ mb: 1 }} id={`message-${message.id}`}>
      <ChatBox
        {...chatBoxProps}
        renderContent={() => <MarkdownProcessor content={deferredContent} />}
      />
    </Box>
  )
}

export default memo(AgentMessageDisplay)
